import {
  Build,
  BuildStatus,
  isBuildRunning,
  isPlatformBuildRunning,
  PlatformBuild,
} from '@todesktop/shared';

export interface NavTabItem {
  key: string;
  label: string;
  to: string;
}

export type AppTabKey =
  | 'overview'
  | 'builds'
  | 'releases'
  | 'analytics'
  | 'settings';

export const getAppTabs = (
  appId: string,
  {
    isBuilderApp,
    isV2App = false,
  }: { isBuilderApp: boolean; isV2App?: boolean },
): NavTabItem[] => {
  const tabs: NavTabItem[] = [
    { key: 'overview', label: 'Overview', to: `/apps/${appId}` },
  ];
  // ToDesktop Builder apps are built in the Builder, not here.
  if (!isBuilderApp) {
    tabs.push({ key: 'builds', label: 'Builds', to: `/apps/${appId}/builds` });
  }
  // v2 apps release through rules: what everyone gets, rollouts, history.
  if (isV2App) {
    tabs.push({
      key: 'releases',
      label: 'Releases',
      to: `/apps/${appId}/releases`,
    });
  }
  tabs.push(
    { key: 'analytics', label: 'Analytics', to: `/apps/${appId}/analytics` },
    { key: 'settings', label: 'Settings', to: `/apps/${appId}/settings` },
  );
  return tabs;
};

export const getActiveAppTab = (
  appId: string,
  pathname: string,
): AppTabKey | null => {
  const base = `/apps/${appId}`;
  if (pathname === base || pathname === `${base}/`) return 'overview';
  if (pathname.startsWith(`${base}/builds`)) return 'builds';
  if (pathname.startsWith(`${base}/releases`)) return 'releases';
  if (pathname.startsWith(`${base}/analytics`)) return 'analytics';
  if (pathname.startsWith(`${base}/settings`)) return 'settings';
  return null;
};

export const getAppSettingsTabs = (appId: string): NavTabItem[] => [
  { key: 'general', label: 'General', to: `/apps/${appId}/settings` },
  {
    key: 'build-deploy',
    label: 'Build and Deploy',
    to: `/apps/${appId}/settings/build-deploy`,
  },
  {
    key: 'certificates',
    label: 'Certificates',
    to: `/apps/${appId}/settings/certificates`,
  },
];

/** A v2 app's settings (V2AppRouter); smoke tests with the v2SmokeTests flag. */
export const getV2AppSettingsTabs = (
  appId: string,
  { smokeTests = false }: { smokeTests?: boolean } = {},
): NavTabItem[] => [
  { key: 'general', label: 'General', to: `/apps/${appId}/settings` },
  { key: 'signing', label: 'Signing', to: `/apps/${appId}/settings/signing` },
  ...(smokeTests
    ? [
        {
          key: 'smoke-tests',
          label: 'Smoke tests',
          to: `/apps/${appId}/settings/smoke-tests`,
        },
      ]
    : []),
];

export const getActiveSettingsTab = (
  appId: string,
  pathname: string,
): string | null => {
  const base = `/apps/${appId}/settings`;
  if (pathname.startsWith(`${base}/build-deploy`)) return 'build-deploy';
  if (pathname.startsWith(`${base}/certificates`)) return 'certificates';
  if (pathname.startsWith(`${base}/signing`)) return 'signing';
  if (pathname.startsWith(`${base}/smoke-tests`)) return 'smoke-tests';
  if (pathname === base || pathname === `${base}/`) return 'general';
  return null;
};

/** Settings that belong to the person signed in. */
export const PROFILE_SETTINGS_TABS: NavTabItem[] = [
  { key: 'profile', label: 'Profile', to: '/settings/profile' },
  {
    key: 'security-tokens',
    label: 'Security tokens',
    to: '/settings/security-tokens',
  },
  {
    key: 'access-tokens',
    label: 'Access tokens',
    to: '/settings/access-tokens',
  },
];

/** Settings that belong to the team that owns the apps. */
export const TEAM_SETTINGS_TABS: NavTabItem[] = [
  { key: 'team-general', label: 'General', to: '/settings/team' },
  { key: 'user-management', label: 'Members', to: '/settings/users' },
  { key: 'billing', label: 'Billing', to: '/settings/billing' },
  { key: 'signing', label: 'Signing', to: '/settings/signing' },
];

export const ACCOUNT_SETTINGS_TABS: NavTabItem[] = [
  ...PROFILE_SETTINGS_TABS,
  ...TEAM_SETTINGS_TABS,
];

/** One "Account settings" list, for people without the accountTeams flag. */
export const LEGACY_ACCOUNT_SETTINGS_TABS: NavTabItem[] = [
  ...PROFILE_SETTINGS_TABS,
  { key: 'billing', label: 'Billing', to: '/settings/billing' },
  { key: 'user-management', label: 'User management', to: '/settings/users' },
  { key: 'signing', label: 'Signing', to: '/settings/signing' },
];

/**
 * The settings tabs without Signing unless the `v2Apps` flag is on: the
 * account's signing identities are for v2 apps, which the flag offers.
 */
export const signingTabsFor = (
  tabs: NavTabItem[],
  v2Apps: boolean,
): NavTabItem[] =>
  v2Apps ? tabs : tabs.filter((tab) => tab.key !== 'signing');

export type AccountSettingsSection = 'profile' | 'team';

export const getActiveAccountSettingsTab = (pathname: string): string | null =>
  ACCOUNT_SETTINGS_TABS.find((tab) => pathname.startsWith(tab.to))?.key ?? null;

export const getAccountSettingsSection = (
  pathname: string,
): AccountSettingsSection | null => {
  const key = getActiveAccountSettingsTab(pathname);
  if (!key) return null;
  return TEAM_SETTINGS_TABS.some((tab) => tab.key === key) ? 'team' : 'profile';
};

type AccountPageKey =
  | 'profile'
  | 'team-general'
  | 'billing'
  | 'signing'
  | 'user-management';
type AppPageKey = 'analytics' | 'general' | 'build-deploy' | 'certificates';

export interface PageSection {
  /** The card's element id; a link to `<page>#<id>` scrolls to the card. */
  id: string;
  /** The card's title, and what ⌘K shows for it. */
  label: string;
  /** Extra words ⌘K should match, e.g. "stripe" for Billing accounts. */
  keywords?: string;
  page: AccountPageKey | AppPageKey;
  /**
   * Set when only some apps show the card: Electron apps, Builder apps, or
   * apps with their own certificates rather than their parent app's.
   */
  only?: 'electron' | 'builder' | 'own-certificates';
}

/**
 * The cards ⌘K can jump to. A card uses its entry through its `section` prop,
 * which sets its id and title. `pnpm check:page-sections` fails when an entry
 * has no card, or a card on these pages has no entry and isn't marked
 * `searchable={false}`.
 */
export const PAGE_SECTIONS = {
  emailNotifications: {
    id: 'email-notifications',
    label: 'Email notifications',
    keywords: 'build release emails unsubscribe',
    page: 'profile',
  },
  password: {
    id: 'password',
    label: 'Password',
    keywords: 'change reset',
    page: 'profile',
  },
  signInMethods: {
    id: 'sign-in-methods',
    label: 'Sign-in methods',
    keywords: 'login google github email link',
    page: 'profile',
  },
  deleteAccount: {
    id: 'delete-account',
    label: 'Delete account',
    keywords: 'remove close',
    page: 'profile',
  },
  teamProfile: {
    id: 'team-profile',
    label: 'Team profile',
    keywords: 'team name logo rename',
    page: 'team-general',
  },
  billingAccounts: {
    id: 'billing-accounts',
    label: 'Billing accounts',
    keywords: 'stripe portal invoices payment card',
    page: 'billing',
  },
  activeSubscriptions: {
    id: 'active-subscriptions',
    label: 'Active subscriptions',
    keywords: 'plan cancel upgrade',
    page: 'billing',
  },
  inviteUser: {
    id: 'invite-user',
    label: 'Invite a user',
    keywords: 'team members add',
    page: 'user-management',
  },
  invitedUsers: {
    id: 'invited-users',
    label: 'Members',
    keywords: 'team users invited permissions pending admin',
    page: 'user-management',
  },
  delegatedAccess: {
    id: 'delegated-access',
    label: 'Teams you’ve joined',
    keywords: 'delegated access shared accounts leave',
    page: 'user-management',
  },
  signingIdentities: {
    id: 'signing-identities',
    label: 'Signing identities',
    keywords: 'developer id certificate notarization api key hsm codesign',
    page: 'signing',
  },
  signingActivity: {
    id: 'signing-activity',
    label: 'Signing activity',
    keywords: 'security audit log changes',
    page: 'signing',
  },
  downloadsChart: {
    id: 'downloads-chart',
    label: 'Downloads',
    keywords: 'platform mac windows linux',
    page: 'analytics',
  },
  releasesChart: {
    id: 'releases-chart',
    label: 'Releases',
    keywords: 'version partial history',
    page: 'analytics',
  },
  buildsChart: {
    id: 'builds-chart',
    label: 'Builds',
    keywords: 'failed succeeded cancelled',
    page: 'analytics',
    only: 'electron',
  },
  buildSizesChart: {
    id: 'build-sizes-chart',
    label: 'Build sizes',
    keywords: 'size',
    page: 'analytics',
    only: 'electron',
  },
  appInformation: {
    id: 'app-information',
    label: 'App information',
    keywords: 'id name package app id',
    page: 'general',
  },
  downloadPage: {
    id: 'download-page',
    label: 'Download page',
    keywords: 'download link website',
    page: 'general',
  },
  securityUpdates: {
    id: 'security-updates',
    label: 'Security updates',
    keywords: 'automatic',
    page: 'general',
    only: 'builder',
  },
  programmaticReleases: {
    id: 'programmatic-releases',
    label: 'Programmatic releases',
    keywords: 'ci release security token bypass api',
    page: 'general',
    only: 'electron',
  },
  appWebhooks: {
    id: 'app-webhooks',
    label: 'App webhooks',
    keywords: 'webhook hmac secret',
    page: 'general',
    only: 'electron',
  },
  accountAccess: {
    id: 'account-access',
    label: 'Account',
    keywords: 'owner billing users access',
    page: 'general',
  },
  dangerZone: {
    id: 'danger-zone',
    label: 'Danger zone',
    keywords: 'delete app remove',
    page: 'general',
  },
  domains: {
    id: 'domains',
    label: 'Domains',
    keywords: 'custom domain dns',
    page: 'build-deploy',
  },
  artifacts: {
    id: 'artifacts',
    label: 'Artifacts',
    keywords: 'installer dmg msi appimage targets',
    page: 'build-deploy',
  },
  environmentVariables: {
    id: 'environment-variables',
    label: 'Environment variables',
    keywords: 'env secrets',
    page: 'build-deploy',
    only: 'electron',
  },
  macCertificates: {
    id: 'mac-certificates',
    label: 'Mac certificates',
    keywords: 'code signing apple notarization macos',
    page: 'certificates',
    only: 'own-certificates',
  },
  windowsCertificate: {
    id: 'windows-certificate',
    label: 'Windows certificate',
    keywords: 'code signing ev azure',
    page: 'certificates',
    only: 'own-certificates',
  },
  linuxPgpKey: {
    id: 'linux-pgp-key',
    label: 'Linux PGP Key (beta)',
    keywords: 'code signing gpg',
    page: 'certificates',
    only: 'own-certificates',
  },
} satisfies Record<string, PageSection>;

export interface NavSectionLink {
  key: string;
  label: string;
  /** The page the section is on, e.g. "Billing". */
  pageLabel: string;
  to: string;
  keywords?: string;
}

const getSectionLinks = (
  pages: NavTabItem[],
  isShown: (section: PageSection) => boolean,
): NavSectionLink[] =>
  pages.flatMap((page) =>
    Object.values<PageSection>(PAGE_SECTIONS)
      .filter((section) => section.page === page.key && isShown(section))
      .map((section) => ({
        key: `${page.key}-${section.id}`,
        label: section.label,
        pageLabel: page.label,
        to: `${page.to}#${section.id}`,
        keywords: section.keywords,
      })),
  );

/** Cards within the account settings pages, for ⌘K. */
export const getAccountSectionLinks = (
  /** The settings tabs the person sees; the legacy list without accountTeams. */
  tabs: NavTabItem[] = ACCOUNT_SETTINGS_TABS,
): NavSectionLink[] => getSectionLinks(tabs, () => true);

/** Cards within an app's analytics and settings pages, for ⌘K. */
export const getAppSectionLinks = (
  appId: string,
  {
    isBuilderApp,
    isElectron,
    inheritsCertificates,
  }: {
    isBuilderApp: boolean;
    isElectron: boolean;
    /** A child app uses its parent app's certificates. */
    inheritsCertificates: boolean;
  },
): NavSectionLink[] =>
  getSectionLinks(
    [
      { key: 'analytics', label: 'Analytics', to: `/apps/${appId}/analytics` },
      ...getAppSettingsTabs(appId).map((tab) => ({
        ...tab,
        label: `Settings: ${tab.label}`,
      })),
    ],
    (section) =>
      (section.only !== 'electron' || isElectron) &&
      (section.only !== 'builder' || isBuilderApp) &&
      (section.only !== 'own-certificates' || !inheritsCertificates),
  );

/**
 * When switching apps, keep the user on the same kind of page. Build detail
 * pages fall back to the build list because build ids are per app.
 */
export const getEquivalentAppPath = (
  newAppId: string,
  pathname: string,
  /** v2 apps' settings tabs exist only for v2 apps (getV2AppSettingsTabs). */
  { isV2App = false }: { isV2App?: boolean } = {},
): string => {
  const base = `/apps/${newAppId}`;
  const v2Tab = pathname.match(
    /^\/apps\/[^/]+\/settings\/(signing|smoke-tests)/,
  )?.[1];
  if (v2Tab && isV2App) return `${base}/settings/${v2Tab}`;
  // v1's other settings tabs don't exist for v2 apps.
  if (isV2App && /^\/apps\/[^/]+\/settings/.test(pathname)) {
    return `${base}/settings`;
  }
  if (/^\/apps\/[^/]+\/builds/.test(pathname)) return `${base}/builds`;
  if (/^\/apps\/[^/]+\/analytics/.test(pathname)) return `${base}/analytics`;
  if (/^\/apps\/[^/]+\/settings\/build-deploy/.test(pathname)) {
    return `${base}/settings/build-deploy`;
  }
  if (/^\/apps\/[^/]+\/settings\/certificates/.test(pathname)) {
    return `${base}/settings/certificates`;
  }
  if (/^\/apps\/[^/]+\/settings/.test(pathname)) return `${base}/settings`;
  return base;
};

export const getBuildIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/apps\/[^/]+\/builds\/([^/]+)/);
  return match ? match[1] : null;
};

const parseTime = (value: unknown): number => {
  if (!value) return 0;
  const time = new Date(value as string).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const PLATFORMS = ['mac', 'windows', 'linux'] as const;

/**
 * Builds that crash or lose their build server can stay "running" in Firestore
 * forever. No real build takes this long, so the bar stops counting a build
 * once it has gone this long without starting anything.
 */
export const STALE_RUNNING_BUILD_MS = 24 * 60 * 60 * 1000;

/**
 * When the build last started something: its creation, or a platform starting.
 * Re-running a failed build keeps `createdAt`, but each platform gets a new
 * `startedAt` when it starts again.
 */
const getLastStartedAt = (build: Build): number =>
  Math.max(
    parseTime(build.createdAt),
    parseTime(build.startedAt),
    ...PLATFORMS.map((platform) => parseTime(build[platform]?.startedAt)),
  );

/** When the bar stops counting a running build, in ms since the epoch. */
export const getRunningBuildStaleAt = (build: Build): number =>
  getLastStartedAt(build) + STALE_RUNNING_BUILD_MS;

export const getRunningBuilds = (
  builds: Record<string, Build>,
  { now }: { now: number },
): Build[] =>
  Object.values(builds)
    .filter(
      (build) =>
        build && isBuildRunning(build) && now < getRunningBuildStaleAt(build),
    )
    .sort((a, b) => parseTime(b.createdAt) - parseTime(a.createdAt));

type PlatformKey = (typeof PLATFORMS)[number];

export const platformLabels: Record<PlatformKey, string> = {
  mac: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

export type PlatformProgressState =
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface PlatformProgress {
  platform: PlatformKey;
  label: string;
  /** 0–100; finished platforms count as 100. */
  percentage: number;
  /** What the platform is doing now, e.g. "Notarizing". */
  activity: string | null;
  state: PlatformProgressState;
}

const clampPercentage = (value: number) =>
  Math.max(0, Math.min(100, Math.round(value)));

const getPlatformState = (
  build: Build,
  platformBuild: PlatformBuild,
): PlatformProgressState => {
  if (isPlatformBuildRunning(platformBuild)) {
    const notStarted =
      !platformBuild.progressPercentage &&
      (build.status === BuildStatus.queued ||
        build.status === BuildStatus.preparation ||
        platformBuild.status === BuildStatus.queued ||
        platformBuild.status === BuildStatus.preparation);
    return notStarted ? 'queued' : 'running';
  }
  if (platformBuild.status === BuildStatus.succeeded) return 'done';
  if (platformBuild.status === BuildStatus.failed) return 'failed';
  return 'cancelled';
};

/** One entry per platform being built, in macOS, Windows, Linux order. */
export const getPlatformProgress = (build: Build): PlatformProgress[] =>
  PLATFORMS.flatMap((platform) => {
    const platformBuild = build[platform] as PlatformBuild | undefined;
    if (!platformBuild || platformBuild.shouldSkip) return [];
    const state = getPlatformState(build, platformBuild);
    return [
      {
        platform,
        label: platformLabels[platform],
        percentage:
          state === 'done'
            ? 100
            : clampPercentage(platformBuild.progressPercentage ?? 0),
        activity:
          state === 'running'
            ? platformBuild.progressActivityName?.trim() || null
            : null,
        state,
      },
    ];
  });

export interface BuildProgress {
  /** Average progress across the platforms being built, 0–100. */
  percentage: number;
  platforms: PlatformProgress[];
  /** Platforms that finished successfully. */
  doneCount: number;
  /**
   * What is holding the build up: the slowest running platform's activity,
   * e.g. "Notarizing macOS".
   */
  activity: string | null;
  queued: boolean;
}

export const getBuildProgress = (build: Build): BuildProgress => {
  const platforms = getPlatformProgress(build);
  const percentage = platforms.length
    ? platforms.reduce((sum, p) => sum + p.percentage, 0) / platforms.length
    : 0;

  const slowest = platforms
    .filter((p) => p.state === 'running')
    .sort((a, b) => a.percentage - b.percentage)[0];

  return {
    percentage: clampPercentage(percentage),
    platforms,
    doneCount: platforms.filter((p) => p.state === 'done').length,
    activity: slowest?.activity ? `${slowest.activity} ${slowest.label}` : null,
    queued:
      // Also true when there are no platforms yet.
      platforms.every((p) => p.state === 'queued') ||
      build.status === BuildStatus.queued ||
      build.status === BuildStatus.preparation,
  };
};

/**
 * The words in the live pill, e.g. "Notarizing macOS · 2 of 3 done". When
 * several builds run at once the pill just counts them.
 */
export const getBuildPillLabel = (
  progress: BuildProgress,
  runningBuildCount = 1,
): string => {
  if (runningBuildCount > 1) return `${runningBuildCount} builds running`;
  if (progress.queued) return 'queued';
  const total = progress.platforms.length;
  if (total > 1 && progress.doneCount > 0) {
    return `${progress.activity ?? 'Building'} · ${progress.doneCount} of ${total} done`;
  }
  if (total > 1) return `Building ${total} platforms`;
  return progress.activity ?? 'Building';
};

export type BuildOutcomeKind = 'released' | 'ready' | 'failed';

export interface BuildOutcome {
  build: Build;
  kind: BuildOutcomeKind;
  /** When the bar stops showing this outcome, in ms since the epoch. */
  expiresAt: number;
}

export const RECENT_OUTCOME_MS = 24 * 60 * 60 * 1000;

const getEndedAt = (build: Build): number =>
  parseTime(build.endedAt) ||
  Math.max(
    0,
    ...PLATFORMS.map((platform) => parseTime(build[platform]?.endedAt)),
  ) ||
  parseTime(build.createdAt);

/**
 * After a build finishes, the bar briefly says how it went so the user can act
 * on it: release it or look at what failed. It goes away once the user has
 * opened that build, after a day, when a newer build starts, or when they say
 * they won't release it.
 */
export const getRecentBuildOutcome = (
  builds: Record<string, Build>,
  { now, seenBuildIds }: { now: number; seenBuildIds: string[] },
): BuildOutcome | null => {
  const newest = Object.values(builds)
    .filter(Boolean)
    .sort((a, b) => parseTime(b.createdAt) - parseTime(a.createdAt))[0];
  if (!newest || isBuildRunning(newest)) return null;
  if (seenBuildIds.includes(newest.id)) return null;
  const expiresAt = getEndedAt(newest) + RECENT_OUTCOME_MS;
  if (now >= expiresAt) return null;

  if (newest.status === BuildStatus.failed) {
    return { build: newest, kind: 'failed', expiresAt };
  }
  if (newest.status === BuildStatus.succeeded) {
    const released = Boolean(newest.releasedAt || newest.partiallyReleasedAt);
    if (!released && newest.releaseDismissedAt) return null;
    return { build: newest, kind: released ? 'released' : 'ready', expiresAt };
  }
  return null;
};
